// Family licences in bulk (migration 20261023000013): give many at once from a
// pasted list or a CSV file, move many end dates at once, and fix a mistyped
// email. Split out of FamilyLicences.tsx, which owns the table and selection.
//
// NOTHING IS DROPPED SILENTLY. The preview counts what will be sent, what is not
// an email and what repeated; the results list every distinct entry with its own
// answer in plain words, including the ones the server did not give.
//
// Adult emails and dates only. No child appears here.

import { useId, useState, type ChangeEvent } from "react";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary } from "../../i18n/dictionary";
import { getPortalData, notifyFamilyInBackground } from "../../data";
import type { BulkLicenceResult, BulkLicenceStatus } from "../../data/types";
import { parseEmailCsv, parseEmailList } from "../../domain/bulkEmails";
import type { Sponsorship } from "../../domain/sponsorship";
import { licenceErrorCopy } from "./FamilyLicences";
import {
  Button,
  Card,
  Chip,
  ConfirmDialog,
  Field,
  INPUT,
  Notice,
  TableCard,
  TD,
  TH,
  endOfDay,
  errorCode,
  todayIso,
  type Tone,
} from "./ui";

type T = ReturnType<typeof getDictionary>["admin"]["licences"];

/** One status in plain words, with a tone that never reads as failure. */
export function bulkStatusLabel(t: T, status: BulkLicenceStatus): { tone: Tone; label: string } {
  switch (status) {
    case "licensed":
      return { tone: "good", label: t.bulkStLicensed };
    case "already_licensed":
      return { tone: "info", label: t.bulkStAlready };
    case "paid":
      return { tone: "info", label: t.bulkStPaid };
    case "invalid":
      return { tone: "attention", label: t.bulkStInvalid };
    case "full":
      return { tone: "attention", label: t.bulkStFull };
    case "suspended":
      return { tone: "muted", label: t.bulkStSuspended };
  }
}

/**
 * The server's rows, then any entry the client already knew was not an email
 * (those are never sent), so every distinct entry has exactly one result.
 */
export function mergeBulkResults(server: readonly BulkLicenceResult[], invalid: readonly string[]): BulkLicenceResult[] {
  const seen = new Set(server.map((r) => r.email.toLowerCase()));
  return [
    ...server,
    ...invalid.filter((e) => !seen.has(e)).map((email) => ({ email, status: "invalid" as const, sponsorshipId: null })),
  ];
}

/** set_sponsorship_end_dates refusals → calm copy. */
export function endDatesErrorCopy(t: T, err: unknown): string {
  const msg = err instanceof Error ? err.message.toLowerCase() : "";
  if (errorCode(err) === "P0001" && msg.includes("allowance reached")) return t.bulkEndAllowance;
  if (errorCode(err) === "P0001" && msg.includes("end in the future")) return t.dateInvalid;
  return licenceErrorCopy(t, err);
}

/** change_sponsorship_email refusals → calm copy. */
export function fixEmailErrorCopy(t: T, err: unknown): string {
  const msg = err instanceof Error ? err.message.toLowerCase() : "";
  if (errorCode(err) === "P0001" && msg.includes("invalid email")) return t.emailInvalid;
  return licenceErrorCopy(t, err);
}

/** Tell each newly licensed family, one after another, never blocking the screen. */
function notifyEach(ids: readonly string[]): void {
  void (async () => {
    for (const sponsorshipId of ids) {
      notifyFamilyInBackground({ kind: "licence", sponsorshipId });
      await new Promise((r) => setTimeout(r, 50));
    }
  })();
}

// ---- Bulk upload ------------------------------------------------------------

export function BulkUpload({
  orgId,
  suspended,
  remaining,
  live,
  defaultEnds,
  onGiven,
}: {
  orgId: string;
  suspended: boolean;
  remaining: number;
  live: boolean;
  defaultEnds: string;
  onGiven: () => Promise<void> | void;
}) {
  const { locale } = useComfort();
  const t = getDictionary(locale).admin.licences;
  const pasteId = useId();
  const fileId = useId();
  const [text, setText] = useState("");
  const [fileNote, setFileNote] = useState("");
  const [endsOn, setEndsOn] = useState(defaultEnds);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<BulkLicenceResult[] | null>(null);

  const parsed = parseEmailList(text);
  const n = parsed.emails.length;
  const canGive = !suspended && !busy && n > 0 && !parsed.overCap;

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    try {
      const content = await readText(file);
      const csv = parseEmailCsv(content);
      setText([...csv.emails, ...csv.invalid].join("\n"));
      setFileNote(fill(t.bulkFileLoaded, { file: file.name }));
    } catch {
      setFileNote("");
      setError(t.bulkFileError);
    }
  }

  async function give() {
    if (!canGive) return;
    if (!endsOn || Date.parse(endOfDay(endsOn)) <= Date.now()) {
      setError(t.dateInvalid);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const server = await getPortalData().grantSponsorshipsBulk(orgId, parsed.emails, endOfDay(endsOn));
      const merged = mergeBulkResults(server, parsed.invalid);
      setResults(merged);
      notifyEach(merged.filter((r) => r.status === "licensed" && r.sponsorshipId).map((r) => r.sponsorshipId!));
      await onGiven();
    } catch (err) {
      setError(licenceErrorCopy(t, err));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setResults(null);
    setText("");
    setFileNote("");
    setError("");
  }

  if (results) {
    const given = results.filter((r) => r.status === "licensed").length;
    return (
      <Card title={t.bulkResultsTitle} id="bulk-results" style={{ marginTop: 20 }}>
        <p role="status" style={{ margin: "0 0 12px", fontWeight: 600, color: "var(--ink-deep)" }}>
          {fill(t.bulkSummary, { given, other: results.length - given })}
        </p>
        <TableCard label={t.bulkResultsTitle} minWidth={320}>
          <thead>
            <tr>
              <th scope="col" style={TH}>{t.bulkColEmail}</th>
              <th scope="col" style={TH}>{t.bulkColResult}</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => {
              const st = bulkStatusLabel(t, r.status);
              return (
                <tr key={r.email}>
                  <td style={{ ...TD, overflowWrap: "anywhere" }}>{r.email}</td>
                  <td style={TD}>
                    <Chip tone={st.tone}>{st.label}</Chip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableCard>
        <div style={{ marginTop: 14 }}>
          <Button variant="secondary" onClick={reset}>
            {t.bulkNewList}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title={t.bulkTitle} id="bulk-licences" style={{ marginTop: 20 }}>
      <p style={{ margin: "0 0 14px", fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.55 }}>{t.bulkIntro}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label htmlFor={pasteId} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
            {t.bulkPasteLabel}
          </label>
          <textarea
            id={pasteId}
            dir="ltr"
            rows={5}
            value={text}
            disabled={suspended}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => {
              setText(e.target.value);
              setError("");
            }}
            style={{ ...INPUT, minHeight: 120, resize: "vertical", lineHeight: 1.5, fontFamily: "var(--font-body)" }}
          />
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 220px", minWidth: 0 }}>
            <label htmlFor={fileId} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
              {t.bulkFileLabel}
            </label>
            <input
              id={fileId}
              type="file"
              accept=".csv,text/csv,text/plain"
              disabled={suspended}
              aria-describedby={`${fileId}-hint`}
              onChange={(e) => void onFile(e)}
              style={{ fontSize: 14, minHeight: 44, maxWidth: "100%" }}
            />
            <span id={`${fileId}-hint`} style={{ fontSize: 13, color: "var(--ink-faint)", lineHeight: 1.45 }}>
              {fileNote || t.bulkFileHint}
            </span>
          </div>
          <Field
            label={t.bulkEndsLabel}
            type="date"
            min={todayIso()}
            value={endsOn}
            disabled={suspended}
            onChange={(e) => setEndsOn(e.target.value)}
            style={{ flex: "0 1 190px" }}
          />
        </div>

        <div role="status" aria-live="polite" data-testid="bulk-preview" style={{ fontSize: 14, color: "var(--ink)", lineHeight: 1.55 }}>
          {parsed.overCap ? (
            <p style={{ margin: 0, fontWeight: 600 }}>{t.bulkOverCap}</p>
          ) : (
            text.trim() !== "" && (
              <>
                <p style={{ margin: 0, fontWeight: 600 }}>{fill(t.bulkReady, { n })}</p>
                {parsed.invalid.length > 0 && <p style={{ margin: "2px 0 0" }}>{fill(t.bulkInvalidNote, { n: parsed.invalid.length })}</p>}
                {parsed.duplicates > 0 && <p style={{ margin: "2px 0 0" }}>{fill(t.bulkDuplicateNote, { n: parsed.duplicates })}</p>}
                {n > remaining && <p style={{ margin: "2px 0 0" }}>{fill(t.bulkLeftNote, { n: remaining })}</p>}
              </>
            )
          )}
        </div>
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--peach-deep)", fontWeight: 600, fontSize: 14 }}>
            {error}
          </p>
        )}
        <div>
          <Button onClick={() => void give()} disabled={!canGive}>
            {busy ? t.bulkGiving : fill(t.bulkGive, { n })}
          </Button>
        </div>
        {!live && <p style={{ margin: 0, fontSize: 12.5, color: "var(--ink-faint)" }}>{t.bulkDemoHint}</p>}
      </div>
    </Card>
  );
}

/** File → text, through FileReader where Blob.text is missing (older engines). */
function readText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ""));
    r.onerror = () => reject(r.error);
    r.readAsText(file);
  });
}

// ---- Bulk end date ------------------------------------------------------------

export function BulkEndDate({
  orgId,
  selected,
  suspended,
  onDone,
  onClear,
}: {
  orgId: string;
  selected: readonly Sponsorship[];
  suspended: boolean;
  onDone: (changed: number) => Promise<void> | void;
  onClear: () => void;
}) {
  const { locale } = useComfort();
  const t = getDictionary(locale).admin.licences;
  const [date, setDate] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (selected.length === 0) return null;

  async function save() {
    if (busy) return;
    if (!date || Date.parse(endOfDay(date)) <= Date.now()) {
      setError(t.dateInvalid);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const n = await getPortalData().setSponsorshipEndDates(
        orgId,
        selected.map((s) => s.id),
        endOfDay(date),
      );
      setDate("");
      await onDone(n);
    } catch (err) {
      setError(endDatesErrorCopy(t, err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="bulk-end-date"
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "flex-end",
        padding: "14px 16px",
        marginBottom: 12,
        background: "var(--surface-warm, var(--surface))",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
      }}
    >
      <b style={{ alignSelf: "center", color: "var(--ink-deep)" }}>{fill(t.selectedCount, { n: selected.length })}</b>
      <Field
        label={t.bulkEndLabel}
        type="date"
        min={todayIso()}
        value={date}
        disabled={suspended}
        error={error || undefined}
        onChange={(e) => {
          setDate(e.target.value);
          setError("");
        }}
        style={{ flex: "1 1 200px" }}
      />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button onClick={() => void save()} disabled={suspended || busy || !date}>
          {fill(t.bulkEndButton, { n: selected.length })}
        </Button>
        <Button variant="secondary" onClick={onClear} disabled={busy}>
          {t.clearSelection}
        </Button>
      </div>
    </div>
  );
}

// ---- Fix a wrong email ----------------------------------------------------------

export function FixEmailDialog({
  row,
  onCancel,
  onDone,
}: {
  row: Sponsorship;
  onCancel: () => void;
  onDone: (email: string) => Promise<void> | void;
}) {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.admin.licences;
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const claimed = row.claimedAt !== null;

  async function save() {
    if (busy) return;
    const clean = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      setError(t.emailInvalid);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await getPortalData().changeSponsorshipEmail(row.id, clean);
      await onDone(clean);
    } catch (err) {
      setError(fixEmailErrorCopy(t, err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      title={t.fixTitle}
      body={
        claimed ? (
          <Notice tone="attention">{fill(t.fixBodyClaimed, { email: row.email })}</Notice>
        ) : (
          fill(t.fixBodyPending, { email: row.email })
        )
      }
      confirmLabel={t.fixButton}
      cancelLabel={dict.common.cancel}
      busy={busy}
      error={error}
      onConfirm={() => void save()}
      onCancel={onCancel}
    >
      <div style={{ marginTop: 14 }}>
        <Field
          label={t.fixLabel}
          type="email"
          dir="ltr"
          autoComplete="off"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
          }}
        />
      </div>
    </ConfirmDialog>
  );
}
